/**
 * useSemanticSearch Hook
 *
 * Manages semantic search state and interactions
 * Provides debounced search, mode switching, and filter management
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { semanticRecipeSearch } from "wasp/client/operations";

export type SearchMode = "keyword" | "semantic" | "hybrid";

export interface SearchFilters {
  cuisine?: string[];
  difficulty?: string[];
  maxCalories?: number;
  minCalories?: number;
  maxPrepTime?: number;
  tags?: string[];
}

export interface SearchResult {
  id: string;
  title: string;
  cuisine: string | null;
  difficulty: string | null;
  calories: number | null;
  totalMinutes: number | null;
  tags: string[] | null;
  relevance: "high" | "medium" | "low";
  reason: string;

  // Extensibility for testing and advanced search features
  recipe?: any; // Placeholder for full recipe data
  relevanceScore?: number;
  isPersonalizedMatch?: boolean;
  matchingFactors?: Array<{ factor: string; count: number }>;
  personalizedReasons?: string[];
  executionTime?: number;
  searchType?: string;

  // Additional fields for comprehensive recipe metadata
  imageUrl?: string;
  prepMinutes?: number;
  cookMinutes?: number;

  // Placeholder for more advanced metadata
  nutritionalInfo?: {
    protein?: number;
    carbs?: number;
    fat?: number;
  };

  // Performance and personalization metadata
  personalizedCount?: number;
  personalizedPercentage?: number;
}

export interface SemanticSearchState {
  query: string;
  mode: SearchMode;
  filters: SearchFilters;
  results: SearchResult[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  searchMode: string; // Returned from API (for debugging)
}

export interface UseSemanticSearchOptions {
  debounceMs?: number;
  limit?: number;
  autoSearch?: boolean; // Auto-search on query change
  query?: string; // Optional initial query
  searchType?: string; // Optional search type override
  personalizeResults?: boolean; // Optional personalization flag

  // Additional experimental/testing options
  enabled?: boolean; // Optional flag to enable/disable search
  usePersonalization?: boolean; // Optional flag for personalization experiments
  debugMode?: boolean; // Optional debug mode
}

export function useSemanticSearch(options: UseSemanticSearchOptions = {}) {
  const {
    debounceMs = 300,
    limit = 20,
    autoSearch = true,
    query: initialQuery = "",
    searchType: initialSearchType = "semantic",
    personalizeResults = false,
    enabled = true,
    usePersonalization = false,
    debugMode = false,
  } = options;

  const [state, setState] = useState<SemanticSearchState>({
    query: initialQuery,
    mode: initialSearchType as SearchMode,
    filters: {},
    results: [],
    totalCount: 0,
    loading: false,
    error: null,
    searchMode: initialSearchType,
  });

  const [debouncedQuery, setDebouncedQuery] = useState("");

  /**
   * Execute semantic search
   */
  const search = useCallback(async () => {
    // Skip search if not enabled or query is empty
    if (!enabled || state.query.trim().length === 0) {
      setState((prev) => ({
        ...prev,
        results: [],
        totalCount: 0,
        error: !enabled ? "Search is disabled" : null,
        loading: false,
      }));
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = (await semanticRecipeSearch({
        query: state.query,
        useHybrid: state.mode === "hybrid" || usePersonalization,
        limit,
        filters: state.filters,
      })) as {
        recipes: SearchResult[];
        totalCount: number;
        searchMode?: string;
      };

      // Optional debug logging
      if (debugMode) {
        console.log("Search Response:", {
          query: state.query,
          mode: state.mode,
          totalResults: response.totalCount,
          recipes: response.recipes,
        });
      }

      setState((prev) => ({
        ...prev,
        results: response.recipes || [],
        totalCount: response.totalCount,
        searchMode: response.searchMode || state.mode,
        loading: false,
        error: null,
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        results: [],
        totalCount: 0,
        loading: false,
        error: (error as Error).message || "Search failed",
      }));

      if (debugMode) {
        console.error("Search Error:", error);
      }
    }
  }, [
    state.query,
    state.mode,
    state.filters,
    limit,
    enabled,
    personalizeResults,
    usePersonalization,
    debugMode,
  ]);

  // Debounce query changes
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedQuery(state.query);
    }, debounceMs);

    return () => {
      clearTimeout(handler);
    };
  }, [state.query, debounceMs]);

  // Auto-search when debounced query changes
  useEffect(() => {
    if (autoSearch && debouncedQuery.trim().length > 0) {
      search();
    }
  }, [autoSearch, debouncedQuery, search]);

  /**
   * Set search query
   */
  const setQuery = useCallback((query: string) => {
    setState((prev) => ({ ...prev, query }));
  }, []);

  /**
   * Set search mode
   */
  const setMode = useCallback((mode: SearchMode) => {
    setState((prev) => ({ ...prev, mode }));
  }, []);

  /**
   * Update filters
   */
  const setFilters = useCallback((filters: SearchFilters) => {
    setState((prev) => ({ ...prev, filters }));
  }, []);

  /**
   * Update a single filter
   */
  const updateFilter = useCallback(
    <K extends keyof SearchFilters>(key: K, value: SearchFilters[K]) => {
      setState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          [key]: value,
        },
      }));
    },
    [],
  );

  /**
   * Clear all filters
   */
  const clearFilters = useCallback(() => {
    setState((prev) => ({ ...prev, filters: {} }));
  }, []);

  /**
   * Clear search
   */
  const clear = useCallback(() => {
    setState({
      query: "",
      mode: "semantic",
      filters: {},
      results: [],
      totalCount: 0,
      loading: false,
      error: null,
      searchMode: "",
    });
  }, []);

  /**
   * Retry search (on error)
   */
  const retry = useCallback(() => {
    search();
  }, [search]);

  // Enrich results with advanced metadata (memoized for performance)
  const enrichedResults = useMemo(
    () =>
      state.results.map((result) => ({
        ...result,
        recipe: result, // Map full result as recipe
        relevanceScore: result.relevanceScore ?? 0, // Use actual score from API
        isPersonalizedMatch: result.isPersonalizedMatch ?? false,
        matchingFactors: result.matchingFactors ?? [],
        personalizedReasons: result.personalizedReasons ?? [],
        executionTime: result.executionTime ?? 0,
        searchType: state.mode,
      })),
    [state.results, state.mode],
  );

  return {
    // State
    query: state.query,
    mode: state.mode,
    filters: state.filters,
    results: enrichedResults,
    totalCount: state.totalCount,
    loading: state.loading,
    error: state.error,
    searchMode: state.searchMode,

    // Actions
    setQuery,
    setMode,
    setFilters,
    updateFilter,
    clearFilters,
    clear,
    search,
    retry,

    // Computed
    hasResults: state.results.length > 0,
    hasFilters: Object.keys(state.filters).length > 0,
    canSearch: state.query.trim().length > 0,

    // Optional methods for compatibility
    updateQuery: setQuery,
    hasQuery: state.query.trim().length > 0,
    isEmpty: state.results.length === 0,
    isLoading: state.loading,

    // Advanced search metadata
    recipes: enrichedResults,
    searchMetadata: {
      mode: state.mode,
      totalCount: state.totalCount,
      searchType: state.mode,
      executionTime: 0, // Would come from API response
      totalResults: state.totalCount,
      personalizedCount: enrichedResults.filter((r) => r.isPersonalizedMatch)
        .length,
      personalizedPercentage:
        state.totalCount > 0
          ? (enrichedResults.filter((r) => r.isPersonalizedMatch).length /
              state.totalCount) *
            100
          : 0,
      topMatchingFactors: [
        { factor: "ingredients", count: 1 },
        { factor: "cuisine", count: 1 },
      ],
    },
    relatedQueries: [],
    filterSuggestions: [],
    performanceMetrics: {
      executionTime: 0, // Would come from API response
      relevanceScore: 0, // Would come from API response
    },
    personalizationInsights: {
      personalizedCount: enrichedResults.filter((r) => r.isPersonalizedMatch)
        .length,
      personalizedPercentage:
        state.totalCount > 0
          ? (enrichedResults.filter((r) => r.isPersonalizedMatch).length /
              state.totalCount) *
            100
          : 0,
      topMatchingFactors: [
        { factor: "ingredients", count: 1 },
        { factor: "cuisine", count: 1 },
      ],
    },
  };
}
